import Head from 'next/head'
import { ErrorBoundary } from 'react-error-boundary'
import { useRouter } from 'next/router'
import { useMemo } from 'react'

import { Spinner } from '@wartek-id/spinner'
import { useToast } from '@wartek-id/toast'
import { ListItem } from '@wartek-id/list'

import {
  useAPIDeleteKinerja,
  useAPIKinerjaProfile,
  useAPIPelaksanaanReset,
} from 'api/kinerja/useAPIKinerja'
import { Layout } from 'components/Layout'
import PageHeader from './PageHeader'
import { useMediaQueryHelper } from 'app/PemetaanKompetensiPage/hooks/useMediaQueryHelper'
import { useAuth } from 'utils/auth'
import useResetToggle from '../hooks/useResetToggle'
import { KinerjaErrorBoundary } from 'app/KinerjaShared/components/KinerjaErrorBoundary'
import { isUserHasRole } from 'app/KinerjaKSPage/utils/profile'
import { PENGELOLAAN_KINERJA_BERANDA_PATH } from 'app/KinerjaShared/constants/path'

import type { FallbackProps } from 'react-error-boundary'
import type { LayoutProps } from 'components/Layout'

interface KinerjaLayoutProps extends LayoutProps {
  pageTitle?: string
}

const title = 'Pengelolaan Kinerja'

export default function WrappedKinerjaLayout(props: KinerjaLayoutProps) {
  const router = useRouter()

  return (
    <ErrorBoundary
      FallbackComponent={(errorProps: FallbackProps) => (
        <KinerjaErrorBoundary
          layoutProps={undefined}
          {...errorProps}
          {...props}
        />
      )}
      resetKeys={[router.asPath]}
    >
      <KinerjaLayout {...props} />
    </ErrorBoundary>
  )
}

function KinerjaLayout({ pageTitle, ...props }: KinerjaLayoutProps) {
  const { isDesktop } = useMediaQueryHelper()
  const { data: profile, isLoading: isProfileLoading } = useAPIKinerjaProfile()
  const { session } = useAuth()

  const { mutateAsync: resetCurrentSKPPerencanaanGuru } = useAPIDeleteKinerja()
  const { mutateAsync: resetCurrentSKPPelaksanaanGuru } =
    useAPIPelaksanaanReset(session?.user?.ptkId)
  const toast = useToast()

  const isDesktopBrowser = useMemo(() => {
    return isDesktop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { enabled: resetEnabled, isLoading: isLoadingToggle } = useResetToggle()

  if (isProfileLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Spinner data-testid="loading-spinner" />
      </div>
    )
  }

  const onResetPerencanaanClick = async () => {
    if (confirm('Apakah Anda yakin ingin mereset data SKP Perencanaan?')) {
      resetCurrentSKPPelaksanaanGuru({
        targetState: '',
      })
      resetCurrentSKPPerencanaanGuru(undefined, {
        onSuccess: () => {
          // force refresh the page if the current page is the SKP homepage
          // (i.e. /pengelolaan-kinerja)
          // otherwise, go to the homepage
          window.location.href = PENGELOLAAN_KINERJA_BERANDA_PATH
        },
        onError: () => {
          toast({
            message: 'Gagal mereset data SKP Perencanaan',
          })
        },
      })
    }
  }

  const onResetPelaksanaanClick = () => {
    if (confirm('Apakah Anda yakin ingin mereset data SKP Pelaksanaan?')) {
      resetCurrentSKPPelaksanaanGuru(
        { targetState: '' },
        {
          onSuccess: () => {
            // force refresh the page if the current page is the SKP homepage
            // (i.e. /pengelolaan-kinerja)
            // otherwise, go to the homepage
            window.location.href = '/pengelolaan-kinerja'
          },
          onError: () => {
            toast({
              message: 'Gagal mereset data SKP Pelaksanaan',
            })
          },
        }
      )
    }
  }

  return (
    <Layout {...props}>
      <Head>
        <title>
          {pageTitle ? `${pageTitle} - ` : ''}
          {title}
        </title>
        {!isDesktopBrowser && (
          <meta
            name="viewport"
            content="width=1440, initial-scale=0.1, maximum-scale=1.0, user-scalable=yes"
          />
        )}
      </Head>
      <PageHeader title={title} className="print:!hidden">
        {isUserHasRole(profile.data, 'GURU') &&
        resetEnabled &&
        !isLoadingToggle ? (
          <>
            <ListItem
              onClick={onResetPerencanaanClick}
              className="cursor-pointer"
            >
              Reset Data Perencanaan
            </ListItem>
            <ListItem
              onClick={onResetPelaksanaanClick}
              className="cursor-pointer"
            >
              Reset Data Pelaksanaan
            </ListItem>
          </>
        ) : (
          // add dummy fragment in order to avoid error
          // when the menu bar is opened and the env variable above is false
          // (i.e. in production)
          <></>
        )}
      </PageHeader>
      {props.children}
    </Layout>
  )
}
